/* ===========================================================
   Opora Tools — Bid Generator  v1.0.0
   Tool #11 — the most strategically important calculator.
   Generates a full cleaning bid with low / target / high
   contract values, cost breakdown, benchmark sanity-check,
   PDF customer quote, and anonymous benchmark data capture.

   Global: window.OporaBidGen
   Requires: opora-tools-core.js, jsPDF (CDN)
   Last reviewed: June 2026

   Math references:
     - APPA Custodial Staffing Guidelines (Level 1–5 factors)
     - BOMA Experience Exchange Report (EER) — cost benchmarks
     - BSCAI Benchmarking Survey — $/sqft ranges
     - BLS OES SOC 37-2011 — labor burden rates
   =========================================================== */

/* ===========================================================
   SECTION 1 — PURE CALCULATION FUNCTIONS
   These are exported for Node.js test environment.
   No DOM access. No side effects.
   =========================================================== */

/* ---- APPA Level multiplier table
   Applied to base production rate (sqft/hr).
   Higher factor = more sqft/hr = less labor = lower quality standard.
   Level 1 = highest quality = most labor = lowest multiplier.
   MUST match opora-tool-production.js exactly.
---- */
var BID_APPA_FACTORS = {
  '1': 0.65,
  '2': 1.00,
  '3': 1.18,
  '4': 1.35,
  '5': 1.55
};

/* ---- Base production rates at APPA Level 2 (sqft/hr)
   MUST match opora-tool-production.js exactly.
---- */
var BID_BASE_RATES = {
  carpet:     3500,
  hard_floor: 4000,
  office:     3500,
  common:     4500
  // restroom: fixture-based, not sqft/hr
};

var BID_AVG_MIN_PER_FIXTURE = 9.0;   // APPA midpoint, minutes per fixture set
var BID_FTE_DIVISOR         = 2080;  // standard annual hours per FTE
var BID_WEEKS_PER_YEAR      = 52;

/* ---- Default area mix percentages (BSCAI industry typical) ---- */
var BID_DEFAULT_MIX = {
  pct_carpet:     40,
  pct_hard_floor: 20,
  pct_restroom:   5,
  pct_office:     25,
  pct_common:     10
};

/* ---- Service frequency → workdays per week ---- */
var BID_FREQUENCY_WORKDAYS = {
  'nightly_5x':  5,
  '3x_weekly':   3,
  '2x_weekly':   2,
  '1x_weekly':   1,
  'weekly':      1
};

/* ---- Seed benchmark data
   Mirrors SEED_BENCHMARKS in opora-tool-benchmarks.js.
   cost_per_sqft_per_year values used for sanity-check.
---- */
var BID_SEED_BENCHMARKS = {
  office: {
    label: 'Office / Commercial',
    cost_per_sqft_per_year: { low: 1.20, median: 1.85, high: 2.75 }
  },
  medical: {
    label: 'Medical / Healthcare',
    cost_per_sqft_per_year: { low: 2.80, median: 4.20, high: 6.50 }
  },
  education: {
    label: 'Education (K-12 / Higher Ed)',
    cost_per_sqft_per_year: { low: 1.40, median: 2.10, high: 3.20 }
  },
  retail: {
    label: 'Retail / Big-Box',
    cost_per_sqft_per_year: { low: 0.85, median: 1.30, high: 2.00 }
  },
  industrial: {
    label: 'Industrial / Manufacturing',
    cost_per_sqft_per_year: { low: 0.60, median: 1.00, high: 1.70 }
  },
  hospitality: {
    label: 'Hospitality / Hotel',
    cost_per_sqft_per_year: { low: 3.20, median: 5.00, high: 8.00 }
  },
  'multi-tenant': {
    label: 'Multi-Tenant / Mixed-Use',
    cost_per_sqft_per_year: { low: 1.50, median: 2.20, high: 3.40 }
  }
};

/* ---- Regional adjustment factors (mirrors opora-tool-benchmarks.js) ---- */
var BID_REGIONAL_FACTORS = {
  national:  1.00,
  northeast: 1.22,
  southeast: 0.90,
  midwest:   0.95,
  southwest: 0.92,
  west:      1.28
};

/* ---- sqftBucket — categorise sqft ---- */
function bidSqftBucket(sqft) {
  var n = Number(sqft) || 0;
  if (n < 25000)  return 'small';
  if (n < 100000) return 'medium';
  if (n < 500000) return 'large';
  return 'x-large';
}

/* ---- slugify — strip special chars for filenames ---- */
function bidSlugify(str) {
  if (!str || typeof str !== 'string') return 'facility';
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60) || 'facility';
}

/**
 * calculateLaborHours(inputs) — pure labor-hour calculation.
 * Replicates calculateProduction() from opora-tool-production.js.
 *
 * inputs: {
 *   total_sqft:     number,
 *   pct_carpet:     number (0-100, default BID_DEFAULT_MIX),
 *   pct_hard_floor: number,
 *   pct_restroom:   number,
 *   pct_office:     number,
 *   pct_common:     number,
 *   fixtures:       number (optional; derived from restroom sqft if 0),
 *   appa_level:     '1'|'2'|'3'|'4'|'5' (default '2'),
 *   workdays:       number (derived from frequency if not passed)
 * }
 *
 * Returns: {
 *   valid: boolean,
 *   errors: string[],
 *   nightly_hours: number,
 *   annual_hours: number,
 *   total_sqft: number,
 *   appa_factor: number,
 *   fixtures_used: number
 * }
 */
function calculateLaborHours(inputs) {
  var errors = [];
  function clampPos(v) { return (!isNaN(v) && v > 0) ? Number(v) : 0; }

  var total = clampPos(inputs.total_sqft || 0);
  if (!total || total < 0) {
    errors.push('Total square footage must be greater than zero.');
    return { valid: false, errors: errors };
  }

  /* Prefer shared production engine (soil/shift/1×-week modifiers stay in sync). */
  var Shared = (typeof global !== 'undefined' && global.OporaCalcShared) ||
    (typeof window !== 'undefined' && window.OporaCalcShared) || null;
  if (Shared && typeof Shared.productionCalc === 'function') {
    var workdaysShared = clampPos(inputs.workdays || 5);
    if (workdaysShared > 7) workdaysShared = 7;
    var shared = Shared.productionCalc({
      mode: 'simple',
      total_sqft: total,
      appa_level: inputs.appa_level || '2',
      workdays: workdaysShared || 5,
      pct_carpet: inputs.pct_carpet,
      pct_hard_floor: inputs.pct_hard_floor,
      pct_restroom: inputs.pct_restroom,
      pct_office: inputs.pct_office,
      pct_common: inputs.pct_common,
      fixtures: inputs.fixtures,
      shift_timing: inputs.shift_timing,
      soil_density: inputs.soil_density
    });
    if (!shared.valid) return shared;
    return {
      valid: true,
      errors: [],
      nightly_hours: shared.nightly_hours,
      annual_hours: shared.annual_hours,
      total_sqft: shared.total_sqft,
      appa_factor: BID_APPA_FACTORS[String(inputs.appa_level || '2')] || 1,
      fixtures_used: shared.fixtures_used,
      modifier_notes: shared.modifier_notes || []
    };
  }

  var appaKey    = String(inputs.appa_level || '2');
  var appaFactor = BID_APPA_FACTORS[appaKey] || 1.00;

  /* Normalize mix percentages */
  var rawPcts = {
    carpet:     clampPos(inputs.pct_carpet     !== undefined ? inputs.pct_carpet     : BID_DEFAULT_MIX.pct_carpet),
    hard_floor: clampPos(inputs.pct_hard_floor !== undefined ? inputs.pct_hard_floor : BID_DEFAULT_MIX.pct_hard_floor),
    restroom:   clampPos(inputs.pct_restroom   !== undefined ? inputs.pct_restroom   : BID_DEFAULT_MIX.pct_restroom),
    office:     clampPos(inputs.pct_office     !== undefined ? inputs.pct_office     : BID_DEFAULT_MIX.pct_office),
    common:     clampPos(inputs.pct_common     !== undefined ? inputs.pct_common     : BID_DEFAULT_MIX.pct_common)
  };
  var pctSum = rawPcts.carpet + rawPcts.hard_floor + rawPcts.restroom +
               rawPcts.office + rawPcts.common;
  var normFactor = pctSum > 0 ? 100 / pctSum : 1;

  var sqft = {
    carpet:     total * (rawPcts.carpet     * normFactor / 100),
    hard_floor: total * (rawPcts.hard_floor * normFactor / 100),
    restroom:   total * (rawPcts.restroom   * normFactor / 100),
    office:     total * (rawPcts.office     * normFactor / 100),
    common:     total * (rawPcts.common     * normFactor / 100)
  };

  /* Effective production rates with APPA adjustment */
  var effRates = {
    carpet:     BID_BASE_RATES.carpet     * appaFactor,
    hard_floor: BID_BASE_RATES.hard_floor * appaFactor,
    office:     BID_BASE_RATES.office     * appaFactor,
    common:     BID_BASE_RATES.common     * appaFactor
  };

  var hrCarpet    = sqft.carpet     > 0 ? sqft.carpet     / effRates.carpet     : 0;
  var hrHardFloor = sqft.hard_floor > 0 ? sqft.hard_floor / effRates.hard_floor : 0;
  var hrOffice    = sqft.office     > 0 ? sqft.office     / effRates.office     : 0;
  var hrCommon    = sqft.common     > 0 ? sqft.common     / effRates.common     : 0;

  /* Restroom hours via fixture time */
  var fixturesExplicit = clampPos(inputs.fixtures || 0);
  var fixturesUsed = fixturesExplicit > 0 ? fixturesExplicit : Math.round(sqft.restroom / 150);
  var hrRestroom = (fixturesUsed * BID_AVG_MIN_PER_FIXTURE) / 60;

  var nightlyHours = hrCarpet + hrHardFloor + hrOffice + hrCommon + hrRestroom;

  var workdays   = clampPos(inputs.workdays || 5);
  if (workdays > 7) workdays = 7;
  /* Fallback path: match shared low-frequency penalty when Shared is unavailable */
  if (workdays <= 1) nightlyHours *= 1.3;

  var annualHours = nightlyHours * workdays * BID_WEEKS_PER_YEAR;

  return {
    valid:          true,
    errors:         [],
    nightly_hours:  nightlyHours,
    annual_hours:   annualHours,
    total_sqft:     total,
    appa_factor:    appaFactor,
    fixtures_used:  fixturesUsed
  };
}

/**
 * calculateBid(inputs) — full bid calculation.
 *
 * inputs: {
 *   // Step 1
 *   facility_type:    string (office|medical|education|retail|industrial|hospitality|multi-tenant)
 *   region:           string (national|northeast|southeast|midwest|southwest|west)
 *   term_months:      number (default 36)
 *   // Step 2
 *   total_sqft:       number
 *   num_floors:       number
 *   num_restrooms:    number
 *   pct_carpet:       number
 *   pct_hard_floor:   number
 *   pct_restroom:     number
 *   pct_office:       number
 *   pct_common:       number
 *   fixtures:         number (optional)
 *   // Step 3
 *   frequency:        string (nightly_5x|3x_weekly|2x_weekly|1x_weekly|weekly)
 *   appa_level:       '1'|'2'|'3'|'4'|'5'
 *   shift_type:       string (after_hours|day_porter|hybrid)
 *   // Step 4
 *   labor_burden:     number ($/hr)
 *   supply_cost_psf:  number ($/sqft/yr, default 0.08)
 *   equipment_annual: number ($)
 *   overhead_pct:     number (%, default 12)
 *   margin_pct:       number (%, default 38)
 * }
 *
 * Returns: {
 *   valid: boolean,
 *   errors: string[],
 *   annual_hours: number,
 *   annual_labor_cost: number,
 *   annual_supply_cost: number,
 *   annual_equipment: number,
 *   subtotal_direct: number,
 *   overhead_cost: number,
 *   total_cost: number,
 *   bid_target: number,
 *   bid_low: number,
 *   bid_high: number,
 *   monthly_invoice: number,
 *   cost_per_sqft_yr: number,
 *   sanity: { status, explanation, benchmark_low, benchmark_high, benchmark_median }
 * }
 */
function calculateBid(inputs) {
  /* Defaults */
  var facilityType   = inputs.facility_type  || 'office';
  var region         = inputs.region         || 'national';
  /* Prefer Base wage + burden % when present; else loaded labor_burden. */
  var laborBurden = 0;
  var _shared = (typeof window !== 'undefined' && window.OporaCalcShared) ||
    (typeof globalThis !== 'undefined' && globalThis.OporaCalcShared) || null;
  if (inputs.state && _shared && typeof _shared.regionFromState === 'function') {
    region = _shared.regionFromState(inputs.state) || region;
  }
  if (_shared && typeof _shared.resolveLoadedLaborRate === 'function') {
    laborBurden = _shared.resolveLoadedLaborRate(inputs);
  } else {
    var baseWage = Number(inputs.base_wage || 0);
    if (baseWage > 0) {
      var burPct = Number(inputs.payroll_burden_pct !== undefined ? inputs.payroll_burden_pct : 50);
      laborBurden = baseWage * (1 + burPct / 100);
    } else {
      laborBurden = Number(inputs.labor_burden || 0);
    }
  }
  /* Contractor cleaning supplies vs paper/soap consumables (pass-through). */
  var hasSplitSupplies = inputs.cleaning_supply_psf !== undefined || inputs.consumable_psf !== undefined;
  var cleaningPsf;
  var consumablePsf;
  var consumableMarkup;
  if (hasSplitSupplies) {
    cleaningPsf = Number(inputs.cleaning_supply_psf !== undefined ? inputs.cleaning_supply_psf : 0.06);
    consumablePsf = Number(inputs.consumable_psf !== undefined ? inputs.consumable_psf : 0.02);
    consumableMarkup = Number(inputs.consumable_markup_pct !== undefined ? inputs.consumable_markup_pct : 8);
  } else {
    cleaningPsf = Number(inputs.supply_cost_psf !== undefined ? inputs.supply_cost_psf : 0.08);
    consumablePsf = 0;
    consumableMarkup = 0;
  }
  var equipmentAnnual = Number(inputs.equipment_annual || 0);
  var overheadPct    = Number(inputs.overhead_pct    !== undefined ? inputs.overhead_pct    : 12);
  var marginPct      = Number(inputs.margin_pct      !== undefined ? inputs.margin_pct      : 38);
  var frequency      = inputs.frequency || 'nightly_5x';
  var workdays       = BID_FREQUENCY_WORKDAYS[frequency] || 5;

  /* Validate */
  var errors = [];
  if (!inputs.total_sqft || Number(inputs.total_sqft) <= 0) {
    errors.push('Total square footage must be greater than zero.');
  }
  if (laborBurden <= 0) {
    errors.push('Labor burden rate must be greater than zero.');
  }
  if (marginPct < 0 || marginPct >= 100) {
    errors.push('Gross margin % must be between 0 and 99.');
  }
  if (errors.length) {
    return { valid: false, errors: errors };
  }

  /* Labor hours */
  var lh = calculateLaborHours({
    total_sqft:     Number(inputs.total_sqft),
    pct_carpet:     inputs.pct_carpet,
    pct_hard_floor: inputs.pct_hard_floor,
    pct_restroom:   inputs.pct_restroom,
    pct_office:     inputs.pct_office,
    pct_common:     inputs.pct_common,
    fixtures:       inputs.fixtures,
    appa_level:     inputs.appa_level,
    workdays:       workdays
  });

  if (!lh.valid) {
    return { valid: false, errors: lh.errors };
  }

  var annualHours      = lh.annual_hours;
  var totalSqft        = lh.total_sqft;

  /* Cost lines — cleaning supplies carry target margin; consumables billed at low markup. */
  var annualLaborCost  = annualHours * laborBurden;
  var annualCleaningCost = totalSqft * cleaningPsf;
  var annualConsumableCost = totalSqft * consumablePsf;
  var consumableBill = annualConsumableCost * (1 + consumableMarkup / 100);
  var annualSupplyCost = annualCleaningCost + annualConsumableCost;
  var opsDirect = annualLaborCost + annualCleaningCost + equipmentAnnual;
  var overheadCost = opsDirect * (overheadPct / 100);
  var opsCost = opsDirect + overheadCost;
  var totalCost = opsCost + annualConsumableCost;

  /* Bid prices: full margin on ops; consumables pass-through + markup. */
  var mTarget = marginPct / 100;
  var mLow    = (marginPct - 8) / 100;
  var mHigh   = (marginPct + 8) / 100;

  if (mLow  >= 1) mLow  = 0.30;
  if (mHigh >= 1) mHigh = 0.90;
  if (mTarget >= 1) mTarget = 0.50;

  var bidTarget = (opsCost / (1 - mTarget)) + consumableBill;
  var bidLow    = (opsCost / (1 - mLow)) + consumableBill;
  var bidHigh   = (opsCost / (1 - mHigh)) + consumableBill;

  var monthlyInvoice  = bidTarget / 12;
  var costPerSqftYr   = bidTarget / totalSqft;

  /* Benchmark sanity-check */
  var benchData   = BID_SEED_BENCHMARKS[facilityType] || BID_SEED_BENCHMARKS['office'];
  var regFactor   = BID_REGIONAL_FACTORS[region] || 1.00;
  var benchLow    = benchData.cost_per_sqft_per_year.low;
  var benchHigh   = benchData.cost_per_sqft_per_year.high;
  var benchMedian = benchData.cost_per_sqft_per_year.median * regFactor;

  var deviation = benchMedian > 0 ? (costPerSqftYr - benchMedian) / benchMedian : 0;
  var absDeviation = Math.abs(deviation);
  var sanityStatus;
  if (absDeviation <= 0.25) {
    sanityStatus = 'safe';
  } else if (absDeviation <= 0.50) {
    sanityStatus = 'caution';
  } else {
    sanityStatus = 'danger';
  }

  var deviationPct = Math.round(deviation * 100);
  var direction    = deviation >= 0 ? 'above' : 'below';
  var sanityExplanation =
    'Your bid is $' + costPerSqftYr.toFixed(2) + '/sqft/yr. ' +
    'Industry benchmark for ' + (benchData.label || facilityType) + ' (' + region + ' region) is ' +
    '$' + benchLow.toFixed(2) + '\u2013$' + benchHigh.toFixed(2) + '/sqft/yr, ' +
    'with a regional median of $' + benchMedian.toFixed(2) + '/sqft/yr. ' +
    'You are ' + Math.abs(deviationPct) + '% ' + direction + ' the median.';

  return {
    valid:              true,
    errors:             [],
    annual_hours:       annualHours,
    fixtures_used:      lh.fixtures_used,
    annual_labor_cost:  annualLaborCost,
    annual_supply_cost: annualSupplyCost,
    annual_cleaning_cost: annualCleaningCost,
    annual_consumable_cost: annualConsumableCost,
    consumable_bill:    consumableBill,
    annual_equipment:   equipmentAnnual,
    subtotal_direct:    opsDirect,
    overhead_cost:      overheadCost,
    total_cost:         totalCost,
    bid_target:         bidTarget,
    bid_low:            bidLow,
    bid_high:           bidHigh,
    monthly_invoice:    monthlyInvoice,
    cost_per_sqft_yr:   costPerSqftYr,
    total_sqft:         totalSqft,
    loaded_labor_rate:  laborBurden,
    sanity: {
      status:           sanityStatus,
      explanation:      sanityExplanation,
      benchmark_low:    benchLow,
      benchmark_high:   benchHigh,
      benchmark_median: benchMedian,
      deviation_pct:    deviationPct,
      direction:        direction
    }
  };
}

function buildAnonymousPayload(inputs, result) {
  var uaRaw = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
  var uaHint = uaRaw.slice(0, 80);
  return {
    facility_type:      inputs.facility_type  || 'office',
    region:             inputs.region         || 'national',
    sqft_bucket:        bidSqftBucket(Number(inputs.total_sqft) || 0),
    quality_level:      'APPA ' + (inputs.appa_level || '2'),
    frequency:          inputs.frequency      || 'nightly_5x',
    cost_per_sqft_yr:   Math.round((result.cost_per_sqft_yr || 0) * 100) / 100,
    labor_burden_rate:  Number(inputs.labor_burden || 0),
    gross_margin_pct:   Number(inputs.margin_pct   !== undefined ? inputs.margin_pct : 38),
    timestamp:          new Date().toISOString(),
    ua_hint:            uaHint
  };
}

function appendBidSubmission(payload) {
  var STORAGE_KEY = 'opora_bid_submissions';
  var MAX_ENTRIES = 50;
  try {
    var raw = (typeof localStorage !== 'undefined') ? localStorage.getItem(STORAGE_KEY) : null;
    var arr = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) arr = [];
    arr.push(payload);
    if (arr.length > MAX_ENTRIES) { arr = arr.slice(arr.length - MAX_ENTRIES); }
    if (typeof localStorage !== 'undefined') { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); }
    return arr;
  } catch (e) { return []; }
}

function sendAnonymousCapture(payload) {
  try {
    return fetch('/apps/proxy/bid-events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    }).then(function (r) { return r.ok; }).catch(function () { return false; });
  } catch (e) { return Promise.resolve(false); }
}

if (typeof window !== 'undefined') {
(function (global) {
  'use strict';

  var DRAFT_KEY  = 'opora_bid_draft';
  var MAX_STEP   = 5;

  var _state = {
    step: 1,
    facility_name:  '',
    facility_type:  'office',
    region:         'national',
    term_months:    36,
    total_sqft:     '',
    num_floors:     1,
    num_restrooms:  '',
    pct_carpet:     40,
    pct_hard_floor: 20,
    pct_restroom:   5,
    pct_office:     25,
    pct_common:     10,
    fixtures:       '',
    frequency:      'nightly_5x',
    appa_level:     '2',
    shift_type:     'after_hours',
    labor_burden:     24,
    supply_cost_psf:  0.08,
    equipment_annual: 0,
    overhead_pct:     12,
    margin_pct:       38,
    show_cost_breakdown: false,
    contribute_anonymous: false
  };

  var _lastResult = null;

  var BG = {
    calculateBid:          calculateBid,
    calculateLaborHours:   calculateLaborHours,
    buildAnonymousPayload: buildAnonymousPayload,
    appendBidSubmission:   appendBidSubmission,
    sendAnonymousCapture:  sendAnonymousCapture,
    bidSqftBucket:         bidSqftBucket,
    bidSlugify:            bidSlugify,
    BID_APPA_FACTORS:      BID_APPA_FACTORS,
    BID_BASE_RATES:        BID_BASE_RATES,
    BID_SEED_BENCHMARKS:   BID_SEED_BENCHMARKS,
    BID_REGIONAL_FACTORS:  BID_REGIONAL_FACTORS,
    BID_FREQUENCY_WORKDAYS: BID_FREQUENCY_WORKDAYS
  };

  function _esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function _fmt(n, d) {
    if (typeof n !== 'number' || isNaN(n)) return '\u2014';
    return n.toLocaleString('en-US', { minimumFractionDigits: d || 0, maximumFractionDigits: d !== undefined ? d : 0 });
  }
  function _fmtC(n, d) {
    if (typeof n !== 'number' || isNaN(n)) return '\u2014';
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: d !== undefined ? d : 2, maximumFractionDigits: d !== undefined ? d : 2 });
  }
  function _getEl(id) { return document.getElementById(id); }
  function _getVal(id) { var el = _getEl(id); return el ? el.value : ''; }
  function _setVal(id, v) { var el = _getEl(id); if (el && v !== null && v !== undefined) el.value = v; }
  function _setHTML(id, h) { var el = _getEl(id); if (el) el.innerHTML = h; }
  function _setText(id, t) { var el = _getEl(id); if (el) el.textContent = t; }
  function _show(id) { var el = _getEl(id); if (el) el.style.display = ''; }
  function _hide(id) { var el = _getEl(id); if (el) el.style.display = 'none'; }

  function _showStep(n) {
    _state.step = n;
    document.querySelectorAll('.bid-step').forEach(function (p) {
      p.style.display = parseInt(p.getAttribute('data-step'), 10) === n ? '' : 'none';
    });
    for (var i = 1; i <= MAX_STEP; i++) {
      var btn = document.querySelector('[data-step-btn="' + i + '"]');
      if (!btn) continue;
      btn.classList.remove('bid-stepper__btn--active', 'bid-stepper__btn--done');
      if (i === n) btn.classList.add('bid-stepper__btn--active');
      else if (i < n) btn.classList.add('bid-stepper__btn--done');
    }
    var mLabel = _getEl('bid-mobile-step-label');
    if (mLabel) mLabel.textContent = 'Step ' + n + ' of ' + MAX_STEP;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    _saveDraft();
    if (n === 5) _renderReview();
  }

  function _saveDraft() {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(_state)); } catch (e) {}
    _saveURLParams();
  }

  function _saveURLParams() {
    try {
      var p = new URLSearchParams();
      p.set('ft', _state.facility_type); p.set('reg', _state.region); p.set('term', _state.term_months);
      p.set('sqft', _state.total_sqft || ''); p.set('fl', _state.num_floors); p.set('nr', _state.num_restrooms || '');
      p.set('pcc', _state.pct_carpet); p.set('phf', _state.pct_hard_floor); p.set('pr', _state.pct_restroom);
      p.set('po', _state.pct_office); p.set('pcm', _state.pct_common); p.set('fix', _state.fixtures || '');
      p.set('freq', _state.frequency); p.set('al', _state.appa_level); p.set('sh', _state.shift_type);
      p.set('lb', _state.labor_burden); p.set('sp', _state.supply_cost_psf); p.set('eq', _state.equipment_annual);
      p.set('oh', _state.overhead_pct); p.set('mg', _state.margin_pct);
      var url = new URL(window.location.href);
      url.search = p.toString();
      window.history.replaceState(null, '', url.toString());
    } catch (e) {}
  }

  function _loadFromURLParams() {
    try {
      var p = new URLSearchParams(window.location.search);
      if (!p.get('ft')) return false;
      var s = _state;
      s.facility_type = p.get('ft') || 'office'; s.region = p.get('reg') || 'national';
      s.term_months = Number(p.get('term')) || 36; s.total_sqft = p.get('sqft') || '';
      s.num_floors = Number(p.get('fl')) || 1; s.num_restrooms = p.get('nr') || '';
      s.pct_carpet = Number(p.get('pcc')) || 40; s.pct_hard_floor = Number(p.get('phf')) || 20;
      s.pct_restroom = Number(p.get('pr')) || 5; s.pct_office = Number(p.get('po')) || 25;
      s.pct_common = Number(p.get('pcm')) || 10; s.fixtures = p.get('fix') || '';
      s.frequency = p.get('freq') || 'nightly_5x'; s.appa_level = p.get('al') || '2';
      s.shift_type = p.get('sh') || 'after_hours'; s.labor_burden = Number(p.get('lb')) || 24;
      s.supply_cost_psf = Number(p.get('sp')) !== 0 ? Number(p.get('sp')) : 0.08;
      s.equipment_annual = Number(p.get('eq')) || 0;
      s.overhead_pct = Number(p.get('oh')) !== 0 ? Number(p.get('oh')) : 12;
      s.margin_pct = Number(p.get('mg')) !== 0 ? Number(p.get('mg')) : 38;
      return true;
    } catch (e) { return false; }
  }

  function _loadDraft() {
    if (_loadFromURLParams()) return true;
    try {
      var raw = localStorage.getItem(DRAFT_KEY);
      if (raw) { var saved = JSON.parse(raw); if (saved && saved.facility_type) { Object.assign(_state, saved); return true; } }
    } catch (e) {}
    return false;
  }

  function _validateStep(n) {
    var errs = [];
    if (n === 1) { if (!_state.facility_type) errs.push('Facility type is required.'); if (!_state.region) errs.push('Region is required.'); }
    if (n === 2) { if (!_state.total_sqft || Number(_state.total_sqft) <= 0) errs.push('Total square footage is required and must be greater than zero.'); }
    if (n === 3) { if (!_state.frequency) errs.push('Cleaning frequency is required.'); if (!_state.appa_level) errs.push('APPA quality level is required.'); }
    if (n === 4) {
      if (!_state.labor_burden || Number(_state.labor_burden) <= 0) errs.push('Labor burden rate must be greater than zero.');
      if (Number(_state.margin_pct) < 0 || Number(_state.margin_pct) >= 100) errs.push('Gross margin % must be between 0 and 99.');
    }
    return errs;
  }

  function _showStepErrors(errs) {
    var box = document.querySelector('.bid-step-errors');
    if (!box) return;
    box.innerHTML = errs.map(function (e) { return '<p>' + _esc(e) + '</p>'; }).join('');
    box.style.display = '';
  }

  function _clearStepErrors() {
    var box = document.querySelector('.bid-step-errors');
    if (!box) return;
    box.innerHTML = ''; box.style.display = 'none';
  }

  function _syncStep(n) {
    if (n === 1) {
      _state.facility_name = _getVal('bid-facility-name');
      _state.facility_type = _getVal('bid-facility-type') || 'office';
      _state.region        = _getVal('bid-region')        || 'national';
      _state.term_months   = Number(_getVal('bid-term-months')) || 36;
    }
    if (n === 2) {
      _state.total_sqft      = _getVal('bid-total-sqft');
      _state.num_floors      = Number(_getVal('bid-num-floors'))    || 1;
      _state.num_restrooms   = _getVal('bid-num-restrooms');
      _state.pct_carpet      = Number(_getVal('bid-pct-carpet'))    || 40;
      _state.pct_hard_floor  = Number(_getVal('bid-pct-hard-floor'))|| 20;
      _state.pct_restroom    = Number(_getVal('bid-pct-restroom'))  || 5;
      _state.pct_office      = Number(_getVal('bid-pct-office'))    || 25;
      _state.pct_common      = Number(_getVal('bid-pct-common'))    || 10;
      _state.fixtures        = _getVal('bid-fixtures');
    }
    if (n === 3) {
      _state.frequency   = _getVal('bid-frequency')   || 'nightly_5x';
      _state.appa_level  = _getVal('bid-appa-level')  || '2';
      _state.shift_type  = _getVal('bid-shift-type')  || 'after_hours';
    }
    if (n === 4) {
      _state.labor_burden     = Number(_getVal('bid-labor-burden'))     || 24;
      _state.supply_cost_psf  = Number(_getVal('bid-supply-cost-psf'))  || 0.08;
      _state.equipment_annual = Number(_getVal('bid-equipment-annual')) || 0;
      _state.overhead_pct     = Number(_getVal('bid-overhead-pct'))     || 12;
      _state.margin_pct       = Number(_getVal('bid-margin-pct'))       || 38;
    }
  }

  function _advance() {
    _syncStep(_state.step);
    var errs = _validateStep(_state.step);
    if (errs.length) { _showStepErrors(errs); return; }
    _clearStepErrors();
    if (_state.step < MAX_STEP) _showStep(_state.step + 1);
  }

  function _back() {
    _syncStep(_state.step);
    _clearStepErrors();
    if (_state.step > 1) _showStep(_state.step - 1);
  }

  function _populateForm() {
    _setVal('bid-facility-name', _state.facility_name); _setVal('bid-facility-type', _state.facility_type);
    _setVal('bid-region', _state.region); _setVal('bid-term-months', _state.term_months);
    _setVal('bid-total-sqft', _state.total_sqft); _setVal('bid-num-floors', _state.num_floors);
    _setVal('bid-num-restrooms', _state.num_restrooms); _setVal('bid-pct-carpet', _state.pct_carpet);
    _setVal('bid-pct-hard-floor', _state.pct_hard_floor); _setVal('bid-pct-restroom', _state.pct_restroom);
    _setVal('bid-pct-office', _state.pct_office); _setVal('bid-pct-common', _state.pct_common);
    _setVal('bid-fixtures', _state.fixtures); _setVal('bid-frequency', _state.frequency);
    _setVal('bid-appa-level', _state.appa_level); _setVal('bid-shift-type', _state.shift_type);
    _setVal('bid-labor-burden', _state.labor_burden); _setVal('bid-supply-cost-psf', _state.supply_cost_psf);
    _setVal('bid-equipment-annual', _state.equipment_annual); _setVal('bid-overhead-pct', _state.overhead_pct);
    _setVal('bid-margin-pct', _state.margin_pct);
  }

  function _renderReview() {
    _syncStep(4);
    var result = calculateBid(_state);
    _lastResult = result;
    if (!result.valid) {
      _setHTML('bid-review-errors', '<div class="bid-step-errors" style="display:block">' + result.errors.map(function (e) { return '<p>' + _esc(e) + '</p>'; }).join('') + '</div>');
      return;
    }
    var facilityName = _state.facility_name || '[Facility]';
    var ftLabel = (BID_SEED_BENCHMARKS[_state.facility_type] || {}).label || _state.facility_type;
    _setText('bid-out-target', _fmtC(result.bid_target, 0));
    _setText('bid-out-low', _fmtC(result.bid_low, 0));
    _setText('bid-out-high', _fmtC(result.bid_high, 0));
    _setText('bid-out-monthly', _fmtC(result.monthly_invoice, 0));
    _setText('bid-out-psf', '$' + result.cost_per_sqft_yr.toFixed(2) + '/sqft/yr');
    _setText('bid-out-sqft', _fmt(result.total_sqft, 0) + ' sqft');
    _setText('bid-out-hours', _fmt(result.annual_hours, 0) + ' hrs/yr');
    var s = result.sanity;
    var pillEl = _getEl('bid-sanity-pill');
    if (pillEl) {
      pillEl.className = 'ot-pill ' + (s.status === 'safe' ? 'ot-pill--safe' : s.status === 'caution' ? 'ot-pill--caution' : 'ot-pill--danger');
      var pillLbl = pillEl.querySelector('.ot-pill__label');
      if (pillLbl) pillLbl.textContent = s.status === 'safe' ? 'Within benchmark range' : s.status === 'caution' ? 'Outside benchmark range \u2014 review inputs' : 'Well outside benchmark range \u2014 verify inputs';
    }
    _setText('bid-sanity-text', s.explanation);
    var benchLink = _getEl('bid-benchmarks-link');
    if (benchLink) benchLink.href = '/pages/cleaning-bid-benchmarks?ft=' + _state.facility_type + '&reg=' + _state.region;
    var bkHtml = '<table class="bid-breakdown-table" aria-label="Cost breakdown"><thead><tr><th>Cost category</th><th class="bid-bk__num">Annual ($)</th><th class="bid-bk__num">% of total cost</th></tr></thead><tbody>';
    function bkRow(label, val, total) {
      var pct = total > 0 ? ((val / total) * 100).toFixed(1) : '\u2014';
      return '<tr><td>' + label + '</td><td class="bid-bk__num">' + _fmtC(val, 0) + '</td><td class="bid-bk__num">' + pct + '%</td></tr>';
    }
    bkHtml += bkRow('Labor (annual hours \u00d7 burden rate)', result.annual_labor_cost, result.total_cost);
    bkHtml += bkRow('Supplies ($' + Number(_state.supply_cost_psf).toFixed(3) + '/sqft/yr)', result.annual_supply_cost, result.total_cost);
    bkHtml += bkRow('Equipment depreciation', result.annual_equipment, result.total_cost);
    bkHtml += '<tr class="bid-bk__subtotal"><td>Subtotal direct cost</td><td class="bid-bk__num">' + _fmtC(result.subtotal_direct, 0) + '</td><td class="bid-bk__num"></td></tr>';
    bkHtml += bkRow('Overhead / admin (' + _state.overhead_pct + '%)', result.overhead_cost, result.total_cost);
    bkHtml += '<tr class="bid-bk__total"><td>Total cost</td><td class="bid-bk__num">' + _fmtC(result.total_cost, 0) + '</td><td class="bid-bk__num">100%</td></tr>';
    bkHtml += '<tr class="bid-bk__bid"><td>Bid \u2014 target (' + _state.margin_pct + '% GM)</td><td class="bid-bk__num">' + _fmtC(result.bid_target, 0) + '</td><td class="bid-bk__num"></td></tr>';
    bkHtml += '</tbody></table>';
    _setHTML('bid-breakdown-body', bkHtml);
    _setHTML('bid-review-errors', '');
  }

  BG.downloadPDF = function () {
    if (!_lastResult || !_lastResult.valid) { _renderReview(); if (!_lastResult || !_lastResult.valid) return; }
    var result = _lastResult;
    if (typeof window.jspdf === 'undefined' || !window.jspdf.jsPDF) { alert('PDF library not loaded. Please ensure jsPDF CDN is available and try again.'); return; }
    var jsPDF = window.jspdf.jsPDF;
    var pdf = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' });
    var pageWidth = pdf.internal.pageSize.getWidth();
    var pageHeight = pdf.internal.pageSize.getHeight();
    var mL = 54, mR = 54, mT = 72, mB = 72;
    var cW = pageWidth - mL - mR;
    var y = mT;
    var pageNum = 1;
    var facilityName = (_state.facility_name && _state.facility_name.trim()) ? _state.facility_name.trim() : '[Facility]';
    var today = new Date().toISOString().slice(0, 10);
    var DISCLAIMER = 'This bid estimate is generated from your inputs and general industry references. Actual costs vary significantly with local labor markets, site conditions, scope detail, and contract terms. Always conduct a thorough site walk and review the full scope of work with the prospective customer before submitting a binding bid. Opora Supply makes no warranty as to bid accuracy or competitive positioning.';
    function _hdr() {
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.setTextColor(80, 80, 80);
      pdf.text('Quotation \u2014 ' + facilityName, mL, 36);
      pdf.text('Page ' + pageNum, pageWidth - mR, 36, { align: 'right' });
      pdf.setDrawColor(180, 180, 180); pdf.line(mL, 42, pageWidth - mR, 42); pdf.setTextColor(0, 0, 0);
    }
    function _ftr() {
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5); pdf.setTextColor(120, 120, 120);
      var fy = pageHeight - 36;
      pdf.line(mL, fy - 6, pageWidth - mR, fy - 6);
      var dl = pdf.splitTextToSize(DISCLAIMER, cW);
      pdf.text(dl, mL, fy); pdf.setTextColor(0, 0, 0);
    }
    function _newPage() { _ftr(); pdf.addPage(); pageNum++; y = mT; _hdr(); }
    function _ensureSpace(n) { if (y + n > pageHeight - mB) _newPage(); }
    function _writeText(text, fontSize, bold, indent) {
      pdf.setFont('helvetica', bold ? 'bold' : 'normal'); pdf.setFontSize(fontSize);
      var x = mL + (indent || 0); var w = cW - (indent || 0);
      var lines = pdf.splitTextToSize(text, w); var lh = fontSize * 1.4;
      _ensureSpace(lines.length * lh + 4); pdf.text(lines, x, y); y += lines.length * lh + 4;
    }
    function _writeRule() { _ensureSpace(12); pdf.setDrawColor(200, 200, 200); pdf.line(mL, y, pageWidth - mR, y); y += 12; }
    _hdr();
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(18); pdf.setTextColor(30, 30, 30);
    pdf.text('QUOTATION', mL, y); y += 28;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(13); pdf.text(facilityName, mL, y); y += 20;
    pdf.setFontSize(10); pdf.setTextColor(100, 100, 100);
    pdf.text('Date: ' + today + '   |   Term: ' + _state.term_months + ' months   |   Valid 30 days from date of issue', mL, y);
    y += 20; pdf.setTextColor(0, 0, 0); _writeRule();
    _writeText('CONTRACT SUMMARY', 11, true, 0); y += 4;
    var lines2col = [
      ['Annual contract value (target)', _fmtC(result.bid_target, 0)],
      ['Annual contract value (low)', _fmtC(result.bid_low, 0)],
      ['Annual contract value (high)', _fmtC(result.bid_high, 0)],
      ['Monthly invoice amount', _fmtC(result.monthly_invoice, 0)],
      ['Total sqft serviced', _fmt(result.total_sqft, 0) + ' sqft'],
      ['Cost per sqft per year', '$' + result.cost_per_sqft_yr.toFixed(2)],
      ['Term length', _state.term_months + ' months'],
      ['Start date', '____________________ (TBD)']
    ];
    lines2col.forEach(function (row) {
      _ensureSpace(16); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9.5);
      pdf.text(row[0] + ':', mL, y); pdf.setFont('helvetica', 'normal'); pdf.text(row[1], mL + 220, y); y += 16;
    });
    y += 8; _writeRule();
    _writeText('SCOPE SUMMARY', 11, true, 0); y += 4;
    var freqLabels = { 'nightly_5x': 'Nightly (5x per week)', '3x_weekly': '3x per week', '2x_weekly': '2x per week', '1x_weekly': '1x per week', 'weekly': 'Weekly' };
    var appaLabels = { '1': 'APPA Level 1 \u2014 Orderly Spotlessness', '2': 'APPA Level 2 \u2014 Ordinary Tidiness', '3': 'APPA Level 3 \u2014 Casual Inattention', '4': 'APPA Level 4 \u2014 Moderate Dinginess', '5': 'APPA Level 5 \u2014 Unkempt Neglect' };
    var shiftLabels = { after_hours: 'After-hours / overnight', day_porter: 'Day porter service', hybrid: 'Hybrid (day porter + evening)' };
    var scopeRows = [
      ['Facility type', (BID_SEED_BENCHMARKS[_state.facility_type] || {}).label || _state.facility_type],
      ['Cleaning frequency', freqLabels[_state.frequency] || _state.frequency],
      ['Quality standard', appaLabels[_state.appa_level] || _state.appa_level],
      ['Shift type', shiftLabels[_state.shift_type] || _state.shift_type],
      ['Annual labor hours', _fmt(result.annual_hours, 0) + ' hrs'],
      ['Estimated fixtures', _fmt(result.fixtures_used, 0)]
    ];
    scopeRows.forEach(function (row) {
      _ensureSpace(16); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9.5);
      pdf.text(row[0] + ':', mL, y); pdf.setFont('helvetica', 'normal'); pdf.text(String(row[1]), mL + 220, y); y += 16;
    });
    var showBreakdown = _getEl('bid-show-breakdown-pdf');
    if (showBreakdown && showBreakdown.checked) {
      y += 8; _writeRule(); _writeText('COST BREAKDOWN (CONFIDENTIAL)', 11, true, 0); y += 4;
      var bkRows = [
        ['Labor (annual hours x burden rate)', result.annual_labor_cost],
        ['Supplies', result.annual_supply_cost],
        ['Equipment depreciation', result.annual_equipment],
        ['Overhead (' + _state.overhead_pct + '%)', result.overhead_cost],
        ['Total cost', result.total_cost],
        ['Gross margin (' + _state.margin_pct + '%)', result.bid_target - result.total_cost],
        ['Bid \u2014 target', result.bid_target]
      ];
      bkRows.forEach(function (row) {
        _ensureSpace(16);
        pdf.setFont('helvetica', row[0].indexOf('target') >= 0 || row[0].indexOf('Total') >= 0 ? 'bold' : 'normal');
        pdf.setFontSize(9.5); pdf.text(row[0] + ':', mL, y); pdf.setFont('helvetica', 'normal');
        pdf.text(_fmtC(row[1], 0), mL + 240, y); y += 16;
      });
    }
    y += 8; _writeRule(); _writeText('TERMS AND CONDITIONS', 11, true, 0); y += 4;
    var termsText = [
      '1. Pricing is valid for 30 days from the date of issue.',
      '2. This quotation is based on the square footage and scope described above. Material changes to scope, occupancy, or service frequency require a revised quotation.',
      '3. Annual price adjustments, if any, shall be limited to CPI-U changes for the applicable region, or [X]%, whichever is lower, with 60 days written notice prior to each renewal anniversary.',
      '4. Contractor shall carry Commercial General Liability, Workers\u2019 Compensation, and Commercial Auto insurance at or above the minimums specified in the scope of work document. Certificates available upon request.',
      '5. Either party may terminate for cause with 30 days\u2019 written notice following a 10-business-day cure period.',
      '[Additional terms per executed service agreement.]'
    ];
    termsText.forEach(function (t) { _writeText(t, 9.5, false, 0); y += 2; });
    y += 12; _writeRule(); _writeText('SIGNATURES', 11, true, 0); y += 8;
    ['Facility Owner / Manager', 'Contractor / Service Provider'].forEach(function (label) {
      _ensureSpace(70); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9.5); pdf.text(label, mL, y); y += 18;
      pdf.setFont('helvetica', 'normal');
      pdf.text('Name: ___________________________________', mL, y); y += 16;
      pdf.text('Title: ___________________________________', mL, y); y += 16;
      pdf.text('Date:  ___________________________________', mL, y); y += 16;
      pdf.text('Signature: ______________________________', mL, y); y += 24;
    });
    _ftr();
    var slug = bidSlugify(facilityName);
    var stamp = (typeof window.OporaFileStamp === 'function')
      ? window.OporaFileStamp()
      : new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    pdf.save('quotation-' + slug + '-' + stamp + '.pdf');
    var contributeToggle = _getEl('bid-contribute-anonymous');
    if (contributeToggle && contributeToggle.checked) { _fireAnonymousCapture(); }
  };

  function _fireAnonymousCapture() {
    if (!_lastResult || !_lastResult.valid) return;
    var payload = buildAnonymousPayload(_state, _lastResult);
    appendBidSubmission(payload);
    sendAnonymousCapture(payload).then(function (ok) {
      if (ok) {
        var conf = _getEl('bid-anon-confirm');
        if (conf) { conf.textContent = 'Thanks \u2014 anonymous benchmark submitted.'; conf.style.display = ''; setTimeout(function () { conf.style.display = 'none'; }, 4000); }
      }
    });
  }

  BG.copyShareLink = function () {
    _syncStep(_state.step); _saveURLParams();
    var link = window.location.href;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(function () { _showToast('Share link copied to clipboard.'); }).catch(function () { _fallbackCopy(link); });
    } else { _fallbackCopy(link); }
    var contributeToggle = _getEl('bid-contribute-anonymous');
    if (contributeToggle && contributeToggle.checked && _lastResult && _lastResult.valid) { _fireAnonymousCapture(); }
  };

  function _fallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text; ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
    document.body.appendChild(ta); ta.focus(); ta.select();
    try { document.execCommand('copy'); _showToast('Copied to clipboard.'); }
    catch (e) { _showToast('Copy failed \u2014 please select and copy manually.'); }
    document.body.removeChild(ta);
  }

  function _showToast(msg) {
    if (window.OporaTools && OporaTools.showToast) { OporaTools.showToast(msg, 'success'); }
  }

  BG.emailToSelf = function () {
    _syncStep(_state.step); _saveURLParams();
    var name = (_state.facility_name && _state.facility_name.trim()) ? _state.facility_name.trim() : 'Your Facility';
    var subject = encodeURIComponent('Cleaning Bid \u2014 ' + name);
    var body = encodeURIComponent('A cleaning bid has been generated for ' + name + '.\n\nView and download the quotation here:\n' + window.location.href + '\n\n(Generated by the Opora Supply Bid Generator.)');
    window.location.href = 'mailto:?subject=' + subject + '&body=' + body;
  };

  BG.submitAnonymous = function () {
    if (!_lastResult || !_lastResult.valid) { _syncStep(4); _lastResult = calculateBid(_state); }
    if (!_lastResult || !_lastResult.valid) return;
    _fireAnonymousCapture();
  };

  BG.init = function () {
    var hasSaved = _loadDraft();
    _populateForm();
    if (hasSaved && _state.total_sqft) {
      var banner = _getEl('bid-resume-banner');
      if (banner) {
        banner.style.display = '';
        var resumeBtn = _getEl('bid-resume-btn');
        var dismissBtn = _getEl('bid-resume-dismiss');
        if (resumeBtn) resumeBtn.addEventListener('click', function () { banner.style.display = 'none'; _populateForm(); _showStep(_state.step || 1); });
        if (dismissBtn) dismissBtn.addEventListener('click', function () { banner.style.display = 'none'; });
      }
    }
    for (var i = 1; i <= MAX_STEP; i++) {
      (function (step) {
        var btn = document.querySelector('[data-step-btn="' + step + '"]');
        if (!btn) return;
        btn.addEventListener('click', function () {
          if (step <= _state.step || btn.classList.contains('bid-stepper__btn--done')) { _syncStep(_state.step); _clearStepErrors(); _showStep(step); }
        });
      })(i);
    }
    document.querySelectorAll('[data-bid-next]').forEach(function (btn) { btn.addEventListener('click', _advance); });
    document.querySelectorAll('[data-bid-back]').forEach(function (btn) { btn.addEventListener('click', _back); });
    var dlPdf = _getEl('bid-download-pdf'); if (dlPdf) dlPdf.addEventListener('click', BG.downloadPDF);
    var shareLink = _getEl('bid-share-link'); if (shareLink) shareLink.addEventListener('click', BG.copyShareLink);
    var emailBtn = _getEl('bid-email'); if (emailBtn) emailBtn.addEventListener('click', BG.emailToSelf);
    var anonBtn = _getEl('bid-submit-anonymous'); if (anonBtn) anonBtn.addEventListener('click', BG.submitAnonymous);
    var bkToggle = _getEl('bid-breakdown-toggle');
    if (bkToggle) {
      bkToggle.addEventListener('click', function () {
        var body = _getEl('bid-breakdown-body');
        if (!body) return;
        var isOpen = body.style.display !== 'none';
        body.style.display = isOpen ? 'none' : '';
        bkToggle.textContent = isOpen ? 'Show cost breakdown' : 'Hide cost breakdown';
      });
    }
    _showStep(_state.step || 1);
  };

  BG._getState = function () { return _state; };
  BG._setState = function (s) { Object.assign(_state, s); };
  BG._renderReview = _renderReview;

  global.OporaBidGen = BG;

}(window));
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    calculateBid:          calculateBid,
    calculateLaborHours:   calculateLaborHours,
    buildAnonymousPayload: buildAnonymousPayload,
    appendBidSubmission:   appendBidSubmission,
    bidSqftBucket:         bidSqftBucket,
    bidSlugify:            bidSlugify,
    BID_APPA_FACTORS:      BID_APPA_FACTORS,
    BID_BASE_RATES:        BID_BASE_RATES,
    BID_SEED_BENCHMARKS:   BID_SEED_BENCHMARKS,
    BID_REGIONAL_FACTORS:  BID_REGIONAL_FACTORS,
    BID_FREQUENCY_WORKDAYS: BID_FREQUENCY_WORKDAYS
  };
}